"use client";

import { Suspense, useState, useEffect, useMemo } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import Link from "next/link";
import type { BusynessPageData, FeederWeight, WeekPressure } from "@/lib/conditions/busyness-data";
import { computeWeeklyPressure } from "@/lib/conditions/busyness-pressure";
import GanttTimeline from "./GanttTimeline";
import AggregateHeatbar from "./AggregateHeatbar";
import QuietestWeeksCallout from "./QuietestWeeksCallout";
import CountryRanking from "./CountryRanking";

type Props = {
  mountainData: BusynessPageData;
  mountainSeason: string;
  waterSeason: string;
  locationList: { slug: string; name: string; country: string; type: "ski" | "surf" | "sailing" }[];
  mountainFeederWeights: Record<string, FeederWeight[]>;
};

function recomputeWeeklyPressure(
  data: BusynessPageData,
  weights: FeederWeight[],
): WeekPressure[] {
  const weightMap = new Map(weights.map((fw) => [fw.countryCode, fw.weight]));
  const bars = data.holidays.flatMap((ch) => ch.holidays);

  return computeWeeklyPressure(
    data.weeklyPressure.map((w) => ({ weekStart: w.weekStart, weekEnd: w.weekEnd })),
    {
      bars,
      weightFor: (cc) => weightMap.get(cc) ?? 0,
      publicHolidaysByCountry: data.publicHolidaysByCountry,
      seasonStart: data.seasonStart,
      seasonEnd: data.seasonEnd,
    },
  );
}

// ---------------------------------------------------------------------------
// Inner component (uses useSearchParams, must be inside Suspense)
// ---------------------------------------------------------------------------

function BusynessClientInner({
  mountainData,
  mountainSeason,
  waterSeason,
  locationList,
  mountainFeederWeights,
}: Props) {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();

  const type = searchParams.get("type") === "water" ? "water" : "mountain";
  const selectedCountry = searchParams.get("country") ?? "";

  const [waterState, setWaterState] = useState<{
    data: BusynessPageData | null;
    feederWeights: Record<string, FeederWeight[]>;
    loading: boolean;
    error: boolean;
  }>({ data: null, feederWeights: {}, loading: false, error: false });

  useEffect(() => {
    // Guard on error too, so a failed fetch does not auto-retry every render.
    if (type !== "water" || waterState.data || waterState.loading || waterState.error) return;
    let cancelled = false;
    queueMicrotask(() => {
      if (!cancelled) setWaterState((s) => ({ ...s, loading: true }));
    });
    fetch("/api/research/busyness")
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json: { data: BusynessPageData; season: string; feederWeightsBySlug: Record<string, FeederWeight[]> }) => {
        if (!cancelled) {
          setWaterState({ data: json.data, feederWeights: json.feederWeightsBySlug, loading: false, error: false });
        }
      })
      .catch(() => {
        if (!cancelled) setWaterState((s) => ({ ...s, loading: false, error: true }));
      });
    return () => {
      cancelled = true;
    };
  }, [type, waterState.data, waterState.loading, waterState.error]);

  function retryWater() {
    setWaterState({ data: null, feederWeights: {}, loading: false, error: false });
  }

  const data = type === "mountain" ? mountainData : waterState.data;
  const season = type === "mountain" ? mountainSeason : waterSeason;
  const feederWeightsBySlug = type === "mountain" ? mountainFeederWeights : waterState.feederWeights;

  const filteredLocations = locationList.filter((loc) =>
    type === "mountain" ? loc.type === "ski" : loc.type === "surf" || loc.type === "sailing"
  );

  const countries = useMemo(() => {
    const seen = new Set<string>();
    return filteredLocations
      .filter((l) => {
        if (seen.has(l.country)) return false;
        seen.add(l.country);
        return true;
      })
      .map((l) => l.country)
      .sort((a, b) => a.localeCompare(b, "en"));
  }, [filteredLocations]);

  const representativeSlug = selectedCountry
    ? (filteredLocations.find((l) => l.country === selectedCountry && feederWeightsBySlug[l.slug]?.length)
       ?? filteredLocations.find((l) => l.country === selectedCountry))?.slug
    : undefined;

  const feederWeights = representativeSlug
    ? feederWeightsBySlug[representativeSlug]
    : undefined;

  // A selected country with no seeded visitor mix falls back to the
  // all-destinations average; surface that honestly rather than implying it
  // is country-specific.
  const usingAverageFallback = !!selectedCountry && !feederWeights?.length;

  const heatbarWeeks = useMemo(() => {
    if (!data) return [];
    if (!selectedCountry || !feederWeights?.length) return data.weeklyPressure;
    return recomputeWeeklyPressure(data, feederWeights);
  }, [data, selectedCountry, feederWeights]);

  const averageFeederWeights = useMemo((): FeederWeight[] => {
    const totals = new Map<string, { total: number; count: number }>();
    for (const weights of Object.values(feederWeightsBySlug)) {
      for (const fw of weights) {
        const existing = totals.get(fw.countryCode) ?? { total: 0, count: 0 };
        existing.total += fw.weight;
        existing.count += 1;
        totals.set(fw.countryCode, existing);
      }
    }
    return Array.from(totals.entries()).map(([countryCode, { total, count }]) => ({
      countryCode,
      weight: total / count,
    }));
  }, [feederWeightsBySlug]);

  function setParam(key: string, value: string | null) {
    const params = new URLSearchParams(searchParams.toString());
    if (value) {
      params.set(key, value);
    } else {
      params.delete(key);
    }
    const qs = params.toString();
    router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
  }

  function handleTypeChange(newType: "mountain" | "water") {
    const params = new URLSearchParams(searchParams.toString());
    if (newType === "mountain") {
      params.delete("type");
    } else {
      params.set("type", "water");
    }
    params.delete("country");
    const qs = params.toString();
    router.replace(`${pathname}${qs ? `?${qs}` : ""}`, { scroll: false });
  }

  return (
    <div className="space-y-6 md:space-y-10">
      {/* Global controls */}
      <div className="flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => handleTypeChange("mountain")}
            className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-colors ${
              type === "mountain"
                ? "bg-navy text-white"
                : "bg-warm-bg text-text-muted hover:bg-cream"
            }`}
          >
            Mountain
          </button>
          <button
            type="button"
            onClick={() => handleTypeChange("water")}
            className={`px-5 py-2 rounded-full text-[13px] font-semibold transition-colors ${
              type === "water"
                ? "bg-navy text-white"
                : "bg-warm-bg text-text-muted hover:bg-cream"
            }`}
          >
            Water
          </button>
        </div>
        <span className="text-[12px] text-text-muted">
          {season} season
        </span>
      </div>

      {type === "water" && waterState.error ? (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <p className="text-[14px] text-text-muted">We could not load water destinations just now.</p>
          <button
            type="button"
            onClick={retryWater}
            className="px-5 py-2.5 rounded-full text-[13px] font-semibold bg-navy text-white transition-colors hover:bg-navy/90"
          >
            Try again
          </button>
        </div>
      ) : !data ? (
        <div className="flex items-center justify-center py-16">
          <div className="text-[14px] text-text-muted animate-pulse">Loading water destinations...</div>
        </div>
      ) : (
        <>
          {/* By week (compact, self-contained) */}
          <section className="rounded-[16px] border border-border-light bg-white p-5 md:p-7">
            <CountryRanking
              weeks={heatbarWeeks}
              holidays={data.holidays}
              locationList={filteredLocations}
              feederWeightsBySlug={feederWeightsBySlug}
              publicHolidaysByCountry={data.publicHolidaysByCountry}
              seasonStart={data.seasonStart}
              seasonEnd={data.seasonEnd}
            />
          </section>

          {/* By destination + school holiday calendar (shared country selector) */}
          <section className="rounded-[16px] border border-border-light bg-white p-5 md:p-7">
            <h2 className="font-serif text-[22px] md:text-[28px] font-bold text-navy mb-1">
              By destination
            </h2>
            <p className="text-[13px] text-text-muted mb-5">
              Pick a destination country to see crowd pressure and the school holidays driving it.
            </p>

            <div className="mb-6">
              <select
                value={selectedCountry}
                onChange={(e) => setParam("country", e.target.value || null)}
                aria-label="Filter by country"
                className="w-full md:w-56 px-4 py-2.5 rounded-[10px] border border-border-light bg-warm-bg/40 text-[13px] text-navy focus:outline-none focus:border-gold/40 appearance-none bg-[url('data:image/svg+xml;charset=utf-8,%3Csvg%20xmlns%3D%22http%3A%2F%2Fwww.w3.org%2F2000%2Fsvg%22%20width%3D%2212%22%20height%3D%2212%22%20viewBox%3D%220%200%2012%2012%22%3E%3Cpath%20d%3D%22M3%205l3%203%203-3%22%20stroke%3D%22%239CA3AF%22%20stroke-width%3D%221.5%22%20fill%3D%22none%22%20stroke-linecap%3D%22round%22%20stroke-linejoin%3D%22round%22%2F%3E%3C%2Fsvg%3E')] bg-[length:12px] bg-[right_12px_center] bg-no-repeat pr-8"
              >
                <option value="">All countries</option>
                {countries.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>

            {usingAverageFallback && (
              <p className="text-[12px] italic text-text-muted/80 mb-3">
                We do not yet have a visitor mix for {selectedCountry}, so this shows the average across all destinations.
              </p>
            )}

            <AggregateHeatbar weeks={heatbarWeeks} />

            {/* Calendar sits inside the same card, separated by a divider */}
            <div className="border-t border-border-light mt-7 pt-6">
              <h3 className="font-serif text-[18px] md:text-[22px] font-bold text-navy mb-1">
                School holiday calendar
              </h3>
              <p className="text-[13px] text-text-muted mb-5">
                {usingAverageFallback
                  ? `We do not yet have a visitor mix for ${selectedCountry}; showing school holidays across all destinations.`
                  : selectedCountry
                    ? `School holidays from the feeder countries that drive crowd pressure in ${selectedCountry}.`
                    : "When key feeder countries are on school holidays, destinations get busy."}
              </p>
              <GanttTimeline
                holidays={data.holidays}
                seasonStart={data.seasonStart}
                seasonEnd={data.seasonEnd}
                feederWeights={feederWeights ?? averageFeederWeights}
              />
            </div>
          </section>

          {/* Quietest good weeks */}
          <QuietestWeeksCallout
            weeks={selectedCountry ? data.quietGoodWeeks.filter((w) => w.country === selectedCountry) : data.quietGoodWeeks}
            type={type}
            selectedLocation={selectedCountry || undefined}
          />
        </>
      )}

      {/* 6. Best Time to Go callout */}
      <div className="bg-gradient-to-r from-gold/[0.06] to-gold/[0.02] border border-gold/20 rounded-[14px] p-5 md:p-6 flex flex-col md:flex-row md:items-center gap-4">
        <div className="flex-1">
          <h3 className="font-serif text-[16px] font-semibold text-navy mb-1">
            This data powers Best Time to Go
          </h3>
          <p className="text-[13px] text-text-muted leading-[1.5]">
            Crowd pressure is combined with conditions scores and pricing to find the optimal weeks for each destination.
          </p>
        </div>
        <Link
          href="/best-time-to-go/ski"
          className="shrink-0 inline-flex items-center justify-center bg-gold text-white px-5 py-2.5 rounded-sm text-[13px] font-semibold no-underline transition-all hover:bg-gold-light hover:-translate-y-0.5"
        >
          View Best Time to Go
        </Link>
      </div>

      {/* Related links */}
      <div className="flex flex-wrap items-center justify-center gap-3 text-[13px] pt-2">
        <span className="text-text-muted">Related:</span>
        <Link
          href={`/conditions/season/${type === "mountain" ? "piste" : "surf"}`}
          className="text-gold no-underline hover:underline"
        >
          {type === "mountain" ? "Ski" : "Surf"} season overview
        </Link>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Outer export with Suspense boundary
// ---------------------------------------------------------------------------

export default function BusynessClient(props: Props) {
  return (
    <Suspense fallback={null}>
      <BusynessClientInner {...props} />
    </Suspense>
  );
}
